import styled, { css } from 'styled-components';
import { IconContainer } from '@anwb/icon';
import { pxToRem } from '@anwb/style-utilities';
import Typography from '@anwb/typography';
import { isValidImageUrl } from '../helpers';
import { baseStateStyles, overrideCoreUIStyles, stateStyles } from './common.styled';
import { pickToken } from './helper';
const CHECKBOX_BOX_SIZE = 24;
const CHECKBOX_SPACING_TOP = 3;
const CHECKBOX_HITBOX_OFFSET_HORIZONTAL = 6;
const CHECKBOX_HITBOX_OFFSET_VERTICAL = 10;
const CHECKBOX_CONTENT_SIZE = 44;
const CHECKBOX_COLOR_INNER_RADIUS_DELTA = 3;
/**
 * Checkbox Basic
 */
export const checkboxBasicStyles = ({ theme, $colorContext, }) => css `
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: ${pxToRem(CHECKBOX_BOX_SIZE)};
	height: ${pxToRem(CHECKBOX_BOX_SIZE)};
	background-color: ${theme.colors.blanc.bgFormField};
	border: ${pxToRem(theme.borderWidth[2])} solid
		${pickToken({
    'on-light': theme.colors.medium.borderFormField,
    'on-medium': theme.colors.medium.borderFormField,
    'on-dark': theme.colors.blanc.bgFormField,
}, $colorContext)};
	border-radius: ${pxToRem(theme.borderRadius.s)};
	color: ${theme.colors.blanc.bgFormField};

	/* increased hitbox */
	&::after {
		content: '';
		position: absolute;
		top: -${pxToRem(CHECKBOX_HITBOX_OFFSET_VERTICAL)};
		left: -${pxToRem(CHECKBOX_HITBOX_OFFSET_HORIZONTAL)};
		width: calc(
			${pxToRem(CHECKBOX_BOX_SIZE)} + (2 * ${pxToRem(CHECKBOX_HITBOX_OFFSET_HORIZONTAL)}) +
				(2 * ${pxToRem(theme.borderWidth[2])})
		);
		height: calc(
			${pxToRem(CHECKBOX_BOX_SIZE)} + (2 * ${pxToRem(CHECKBOX_SPACING_TOP)}) +
				(2 * ${pxToRem(CHECKBOX_HITBOX_OFFSET_VERTICAL)}) + (2 * ${pxToRem(theme.borderWidth['2'])})
		);
	}

	${overrideCoreUIStyles}

	${stateStyles({
    default: {
        defaultHover: css `
				border-color: ${pickToken({
            'on-light': theme.colors.highlight.borderFormFieldHover,
            'on-medium': theme.colors.highlight.borderFormFieldHover,
            'on-dark': theme.colors.highlight.borderFormFieldOnDarkHover,
        }, $colorContext)};
				border-width: ${theme.borderWidth.m}px;
				transition: border-width ${theme.timing['100']}ms cubic-bezier(${theme.easings.guide});
			`,
        defaultChecked: css `
				border: none;
				color: ${theme.colors.blanc.iconInteractiveInverse};
				background-color: ${pickToken({
            'on-light': theme.colors.highlight.bgRadiocheckboxSelected,
            'on-medium': theme.colors.highlight.bgRadiocheckboxSelected,
            'on-dark': theme.colors.highlight.bgRadiocheckboxSelectedOnDark,
        }, $colorContext)};
				transition: background-color ${theme.timing['100']}ms cubic-bezier(${theme.easings.guide});
			`,
        defaultHoverChecked: css `
				background-color: ${pickToken({
            'on-light': theme.colors.highlight.bgRadiocheckboxSelectedHover,
            'on-medium': theme.colors.highlight.bgRadiocheckboxSelectedHover,
            'on-dark': theme.colors.highlight.bgRadiocheckboxSelectedHoverOnDark,
        }, $colorContext)};
			`,
    },
    error: {
        errorBase: css `
				border-color: ${pickToken({
            'on-light': theme.colors.error.borderFormField,
            'on-medium': theme.colors.error.borderFormField,
            'on-dark': theme.colors.error.borderFormFieldOnDark,
        }, $colorContext)};

				${IconContainer} {
					transition: color ${theme.timing['100']}ms cubic-bezier(${theme.easings.guide});
				}
			`,
        errorHover: css `
				border-width: ${theme.borderWidth.m}px;
				transition: border-width ${theme.timing['100']}ms cubic-bezier(${theme.easings.guide});
			`,
        errorChecked: css `
				align-self: center;
				background-color: ${theme.colors.blanc.bgFormField};
				color: ${theme.colors.error.iconError};
			`,
    },
    disabled: {
        disabledBase: css `
				background-color: ${pickToken({
            'on-light': theme.colors.light.bgRadiocheckboxDisabled,
            'on-medium': theme.colors.medium.bgRadiocheckboxDisabled,
            'on-dark': theme.colors.light.bgRadiocheckboxDisabled,
        }, $colorContext)};
				color: ${pickToken({
            'on-light': theme.colors.light.bgRadiocheckboxDisabled,
            'on-medium': theme.colors.medium.bgRadiocheckboxDisabled,
            'on-dark': theme.colors.light.bgRadiocheckboxDisabled,
        }, $colorContext)};
				border: none;
				cursor: not-allowed;
			`,
        disabledChecked: css `
				border: none;
				cursor: not-allowed;
				background-color: ${pickToken({
            'on-light': theme.colors.light.bgRadiocheckboxDisabled,
            'on-medium': theme.colors.medium.bgRadiocheckboxDisabled,
            'on-dark': theme.colors.light.bgRadiocheckboxDisabled,
        }, $colorContext)};
				color: ${pickToken({
            'on-light': theme.colors.medium.iconInactive,
            'on-medium': theme.colors.light.bgRadiocheckboxDisabled,
            'on-dark': theme.colors.medium.iconInactive,
        }, $colorContext)};
			`,
    },
    focus: css `
			outline: ${theme.borderWidth.m}px solid
				${pickToken({
        'on-light': theme.colors.highlight.borderFocus,
        'on-medium': theme.colors.highlight.borderFocus,
        'on-dark': theme.colors.highlight.borderFocusOnDark,
    }, $colorContext)};
			outline-offset: ${theme.borderWidth.m}px;
		`,
})}
`;
export const CheckboxBasic = styled.span `
	${checkboxBasicStyles}
`;
/**
 * Checkbox Size
 */
export const checkboxSizeContainerStyles = ({ theme }) => css `
	display: flex;
	justify-content: center;
	align-items: center;
	width: auto;
	height: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	min-width: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	box-sizing: border-box;
	text-align: center;
	border: ${pxToRem(theme.borderWidth[2])} solid ${theme.colors.medium.borderFormField};
	box-shadow: inset 0 0 0 ${pxToRem(theme.borderWidth[2])} ${theme.colors.blanc.bgFormField};
	border-radius: ${pxToRem(theme.borderRadius.m)};

	${overrideCoreUIStyles}

	// Override Core UI 
	&&& {
		padding: 0 ${pxToRem(theme.spacing[200])};
	}

	${baseStateStyles({
    hover: css `
			border-color: ${theme.colors.highlight.borderSelected};
			outline: 0;
		`,
    checked: css `
			border-color: ${theme.colors.action.borderClicked};
			color: ${theme.colors.action.textActive};
		`,
    disabled: css `
			background-color: ${theme.colors.extralight.bgFormFieldDisabled};
			color: ${theme.colors.medium.textInactive};
		`,
    error: css `
			border-color: ${theme.colors.error.borderFormField};
		`,
    focus: css `
			outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocus};
			outline-offset: ${theme.borderWidth.m}px;
		`,
})}
`;
export const CheckboxSizeContainer = styled(Typography).attrs({
    variant: 'button-link',
    tagName: 'span',
}) `
	${checkboxSizeContainerStyles}
`;
/**
 * Checkbox Color
 */
export const checkboxColorContainerStyles = ({ theme, $color, }) => css `
	position: relative;
	height: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	width: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	border: ${pxToRem(theme.borderWidth[2])} solid ${theme.colors.medium.borderFormField};
	border-radius: ${pxToRem(theme.borderRadius.m)};
	box-sizing: border-box;

	${overrideCoreUIStyles}

	&:after {
		content: '';
		position: absolute;
		margin: ${pxToRem(theme.borderWidth[2])};
		height: ${pxToRem(CHECKBOX_CONTENT_SIZE - 4 * Number(theme.borderWidth['2']))};
		width: ${pxToRem(CHECKBOX_CONTENT_SIZE - 4 * Number(theme.borderWidth['2']))};
		border-radius: ${pxToRem(Number(theme.borderRadius.m) - CHECKBOX_COLOR_INNER_RADIUS_DELTA)};
		overflow: hidden;

		${isValidImageUrl($color)
    ? `background-image: url(${$color});`
    : `background-color: ${$color};`}
	}
	${baseStateStyles({
    hover: css `
			border-color: ${theme.colors.highlight.borderSelected};
			outline: 0;
		`,
    checked: css `
			border-color: ${theme.colors.action.borderClicked};
		`,
    disabled: css `
			opacity: 0.5;
		`,
    error: css `
			border-color: ${theme.colors.error.borderFormField};
		`,
    focus: css `
			outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocus};
			outline-offset: ${theme.borderWidth.m}px;
		`,
})}
`;
export const CheckboxColorContainer = styled.span `
	${checkboxColorContainerStyles}
`;
//# sourceMappingURL=checkbox.styled.js.map